import { useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { ToastType } from '@alefeducation/ui-elements-library-core';
import { useAvatarHistoryQuery } from './useAvatarHistoryQuery';
import { AvatarHistory } from './types';
import {
  AvatarFilterType,
  AvatarUploadContextType,
  defaultValues,
} from '../../components/AvatarUpload/context';
import {
  DELETE_AVATARS,
  DeleteAvatarsResponse,
} from '../../apollo/queries/avatarUpload/uploadHistory.queries';
import {
  DELETE_AVATARS_DESCRIPTION,
  DELETE_AVATARS_TITLE,
  deleteAvatarDescription,
  deleteAvatarTitle,
  SOMETHING_WENT_WRONG_MESSAGE,
} from '../../utils/constants';
import { injectAutoHideToastOfType } from '../../utils/Toast';

type ReturnType = {
  avatarHistory: AvatarHistory[];
  loading: boolean;
  error: boolean;
  filters: AvatarFilterType;
  hasSelectedFilter: boolean;
  isAvatarDeleteModalOpen: boolean;
  onRefresh: () => void;
  toggleAvatarDeleteModal: () => void;
  getDeleteAvatarModalDetails: () => { title: string; description: string };
} & AvatarUploadContextType;

export const useAvatarUpload = (): ReturnType => {
  const { history, loading, error, refetch } = useAvatarHistoryQuery();
  const [isAvatarDeleteModalOpen, setIsAvatarDeleteModalOpen] = useState(false);
  const [selectedAvatarFileIds, setSelectedAvatarFileIds] = useState<string[]>(
    [],
  );
  const selectedAvatar = useRef<string | null>(null);
  const [deleteAvatarLoading, setDeleteAvatarLoading] = useState(false);

  const [deleteAvatars] = useMutation<DeleteAvatarsResponse>(DELETE_AVATARS, {
    onCompleted: (data) => {
      const isSuccess = data?.deleteAvatar?.success;
      const type = isSuccess ? 'success' : 'error';
      const { description, title } = getDeleteAvatarToastDetails(type);
      const updatedTitle = isSuccess
        ? title
        : 'We could not delete some of the avatars.';

      injectAutoHideToastOfType(
        'avatar-upload-toast',
        updatedTitle,
        isSuccess ? ToastType.SUCCESS : ToastType.ERROR,
        description,
      );

      refetch();
      setSelectedAvatarFileIds([]);
      selectedAvatar.current = null;
      toggleAvatarDeleteModal();
      setDeleteAvatarLoading(false);
    },
    onError: (e) => {
      const { description, title } = getDeleteAvatarToastDetails('error');

      injectAutoHideToastOfType(
        'avatar-upload-toast',
        title,
        ToastType.ERROR,
        description,
      );

      toggleAvatarDeleteModal();
      setDeleteAvatarLoading(false);
    },
  });

  const [filters, setFilters] = useState<AvatarFilterType>({
    ...defaultValues.filters,
  });

  const onFilterChange = (id: string, value: string) => {
    setFilters((prevState) => ({
      ...prevState,
      [id]: value,
    }));
  };

  const resetFilters = () => setFilters({ ...defaultValues.filters });

  const isFilterEmpty = () => {
    return Object.values(filters).every((filter) => filter === '');
  };

  const filterByOrganization = (avatar: AvatarHistory) => {
    return (
      !filters.organization ||
      avatar.organizations.find((o) => o.id === filters.organization)
    );
  };
  const filterByTenants = (tenants: string[]) => {
    return !filters.tenant || tenants.includes(filters.tenant);
  };

  const filterByGender = (genders: string[]) => {
    return (
      !filters.gender ||
      genders.map((g) => g.toLowerCase()).includes(filters.gender.toLowerCase())
    );
  };

  const getFilteredHistory = () => {
    if (isFilterEmpty()) {
      return history;
    }

    return history.filter((avatar: AvatarHistory) => {
      const filterByField = (
        key: keyof Pick<AvatarHistory, 'category' | 'status'>,
      ) => {
        const filteredValue = (filters as any)[key];
        return (
          !filteredValue ||
          avatar[key].toLowerCase() === filteredValue.toLowerCase()
        );
      };

      return (
        filterByField('category') &&
        filterByGender(avatar.genders) &&
        filterByField('status') &&
        filterByOrganization(avatar) &&
        filterByTenants(avatar.tenants)
      );
    });
  };

  const onAvatarDelete = (fileIds: string | string[]) => {
    if (Array.isArray(fileIds)) {
      selectedAvatar.current = null;
      setSelectedAvatarFileIds(fileIds);
    } else {
      selectedAvatar.current = fileIds;
    }

    toggleAvatarDeleteModal();
  };

  const avatarHistory = getFilteredHistory();
  const getDeleteAvatarModalDetails = (): {
    title: string;
    description: string;
  } => {
    if (
      selectedAvatarFileIds.length === 1 ||
      selectedAvatar?.current !== null
    ) {
      const selectedAvatarFileId =
        selectedAvatar?.current || selectedAvatarFileIds[0];
      const avatar = avatarHistory.find(
        (avatar) => avatar.fileId === selectedAvatarFileId,
      );

      return {
        title: deleteAvatarTitle(avatar?.fileName) as string,
        description: deleteAvatarDescription(avatar?.fileName) as string,
      };
    }
    return {
      title: DELETE_AVATARS_TITLE,
      description: DELETE_AVATARS_DESCRIPTION,
    };
  };

  const getDeleteAvatarToastDetails = (
    type: 'error' | 'success' | 'partial-delete',
  ): { title: string; description: string } => {
    let title = 'Successfully Deleted';
    let description = '';

    const selectedAvatarFileId =
      selectedAvatar?.current || selectedAvatarFileIds[0];

    const avatar = avatarHistory.find(
      (avatar) => avatar.fileId === selectedAvatarFileId,
    );

    const isSingleDelete =
      selectedAvatarFileIds.length === 1 || selectedAvatar?.current !== null;
    switch (type) {
      case 'success':
        description = isSingleDelete
          ? `You have successfully deleted ${avatar!.fileName}.`
          : 'You have successfully deleted avatars.';
        break;

      case 'error':
        title = SOMETHING_WENT_WRONG_MESSAGE;
        description = isSingleDelete
          ? `We could not delete ${avatar!.fileName} right now.`
          : 'We could not delete avatars right now.';
        break;
    }

    return { title, description };
  };

  const deleteModal = {
    loading: deleteAvatarLoading,
    onDelete: () => {
      const ids = selectedAvatar.current
        ? [selectedAvatar.current]
        : selectedAvatarFileIds;

      setDeleteAvatarLoading(true);
      deleteAvatars({ variables: { fileIds: ids } });
    },
  };

  const toggleAvatar = (type: 'single' | 'all', fileId?: string) => {
    if (type === 'single' && fileId) {
      setSelectedAvatarFileIds((prevState): string[] => {
        return prevState.includes(fileId)
          ? prevState.filter((prevFileId) => prevFileId !== fileId)
          : [...prevState, fileId];
      });
    } else if (type === 'all') {
      const allFileIds: string[] = avatarHistory.map((avatar) => avatar.fileId);
      setSelectedAvatarFileIds((prevState) =>
        prevState.length === allFileIds.length ? [] : allFileIds,
      );
    }
  };

  const toggleAvatarDeleteModal = () =>
    setIsAvatarDeleteModalOpen((prevState) => !prevState);

  return {
    avatarHistory,
    loading,
    error: !!error,
    filters,
    isAvatarDeleteModalOpen,
    selectedAvatarFileIds,
    areAllSelected:
      !loading && selectedAvatarFileIds.length === avatarHistory.length,
    onFilterChange,
    resetFilters,
    onRefresh: refetch,
    hasSelectedFilter: !isFilterEmpty(),
    onAvatarDelete,
    toggleAvatarDeleteModal,
    getDeleteAvatarModalDetails,
    toggleAvatar,
    deleteModal,
  };
};
